/**
 * What a failed request on the Resume page means. Only a server's answer decides anything: a 401
 * means no one is logged in, and a refusal is final. A request that got no answer, or a server
 * error, decides nothing, so the tab keeps what it has and offers to try again.
 */
import { isAxiosError } from 'axios'

/**
 * Request options for a caller that decides what every failure means: no global error notice, and
 * no global 403 or 404 page. Those pages need a login, so from a logged-out page they would go
 * straight on to Cognito. The org logo, for one, answers 404 for an org without one.
 */
export const SELF_HANDLED_FAILURES = {
  mercatusNotifyOnError: false,
  ignore403: true,
  ignore404: true,
} as const

/** The server answered that no one is logged in. */
export function isUnauthorized(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 401
}

/** Statuses that ask the client to try again. */
const RETRY_STATUSES: ReadonlySet<number> = new Set([408, 425, 429])

/**
 * The request got no answer, a server error, or a status that asks the client to try again: 408
 * Request Timeout, 425 Too Early or 429 Too Many Requests. Trying again may work.
 */
export function isTransientFailure(error: unknown): boolean {
  if (!isAxiosError(error) || !error.response) {
    return true
  }
  const { status } = error.response
  return status >= 500 || RETRY_STATUSES.has(status)
}
